import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams, useNavigate, useLocation } from 'react-router-dom';

import { ReactComponent as DeleteIcon } from '../../../assets/icons/delete-icon.svg';
import { ReactComponent as EditIcon } from '../../../assets/icons/edit-pencil.svg';
import { ReactComponent as SlashIcon } from '../../../assets/icons/slash.svg';
import { ReactComponent as ExpandIcon } from '../../../assets/icons/tables/expand-icon.svg';
import { ReactComponent as NewTabIcon } from '../../../assets/icons/tables/new-tab.svg';
import { ReactComponent as TickCircleIcon } from '../../../assets/icons/tick-circle-icon.svg';
import { useAppDispatch } from '../../../redux/hooks';
import { setNotificationMessage } from '../../../redux/slices/NotificationManagementSlice';
import { setNotificationDetails } from '../../../redux/slices/NotificationSlice';
import { permissionCheck } from '../../../shared-functions/JWTDocodeFunction';
import deviceApi from '../../../utils/api/partner/device-partner';
import chargingStation from '../../../utils/api/partner/station';
import userApi from '../../../utils/api/users';
import DeleteWarning from '../../delete-warning/DeleteWarning';
import SuccessNotification from '../../success-notification/SuccessNotification';

export interface UseTableSidePanelOptions {
  onRefresh?: () => void;
  onOpenAccountDetails?: ((session: any, newTab: boolean) => void) | null;
  onOpenStationDetails?: ((session: any, newTab: boolean) => void) | null;
  onOpenLocationDetails?: ((session: any, newTab: boolean) => void) | null;
  onOpenDeviceDetails?: ((session: any, newTab: boolean) => void) | null;
  onOpenEndConsumerDetails?: ((userCustomId: string, newTab: boolean) => void) | null;
  onDeleteSuccess?: ((type: 'station' | 'device', id: string) => void) | null;
  onEditDevice?: ((device: any) => void) | null;
  onStatusChange?: ((device: any) => void) | null;
  onDeleteDevice?: ((device: any) => void) | null;
  sessions?: any[];
  /** When true, hides the Edit button in renderSidePanelTopLeftContent */
  disableEdit?: boolean;
  /** When true, hides the Delete button in renderSidePanelTopLeftContent */
  disableDelete?: boolean;
  /** When true, hides the Expand button in renderSidePanelTopLeftContent */
  disableExpand?: boolean;
  /** When true, indicates that the parent component is in partner context */
  isPartner?: boolean;
}

/**
 * Custom React Hook to manage side panel states and actions.
 * @param {any} options configuration options for details expands and success refresh callbacks
 * @returns {any} side panel states and actions
 */
export const useTableSidePanel = (options: UseTableSidePanelOptions = {}) => {
  const {
    onRefresh,
    onOpenAccountDetails,
    onOpenStationDetails,
    onOpenDeviceDetails,
    onOpenEndConsumerDetails,
    onDeleteSuccess,
    onEditDevice,
    onStatusChange,
    onDeleteDevice,
    sessions = [],
    disableEdit = false,
    disableDelete = false,
    disableExpand = false,
    isPartner = false,
  } = options;

  const { t } = useTranslation(['sidePanel', 'account', 'station', 'devices']);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParameters, setSearchParameters] = useSearchParams();
  // Derive edit mode directly from the URL — no separate state needed.
  // This guarantees isEditMode is always false when the panel key changes,
  // with no flash or async delay from useEffect.
  const isEditMode = searchParameters.get('action') === 'edit';
  const [openStatus, setOpenStatus] = useState<boolean>(false);

  const [modalView, setModalView] = useState<{
    title: string;
    header: boolean;
    rightCorner: string;
    notification: boolean;
    content: React.JSX.Element | null;
  }>({
    title: '',
    header: false,
    rightCorner: '',
    notification: false,
    content: null,
  });

  /**
   * Internal default handler to open account details page
   * @param {any} session - The session
   */
  const handleOpenAccountDetails = (session: any) => {
    const accountCustomId = session?.accountCustomId;
    if (!accountCustomId) return;

    const searchParametersWithoutKey = new URLSearchParams(location.search);
    searchParametersWithoutKey.delete('accountCustomId');
    searchParametersWithoutKey.delete('action');
    const cleanSource =
      location.pathname +
      (searchParametersWithoutKey.toString() ? `?${searchParametersWithoutKey.toString()}` : '');

    navigate(`/ev-admin/accounts/partners/details/payout-management/${accountCustomId}`, {
      state: {
        source: cleanSource,
      },
    });
  };

  /**
   * Internal default handler to open station details page
   * @param {any} session - The session
   */

  /**
   * Open station side panel
   * @param {any} session - The session
   */
  const handleOpenStationSidePanel = (session: any) => {
    const stationId = session?.chargingStation || session?.chargingStationId || session?._id;
    if (!stationId) return;
    const newParameters = new URLSearchParams(searchParameters);
    const sidePanelKeys = [
      'accountCustomId',
      'deviceId',
      'deviceHubId',
      'connectorDeviceId',
      'stationId',
      'facilityId',
      'userCustomId',
      'userId',
      'utilityId',
      'transactionId',
      'personalChargerId',
      'payoutId',
      'cdrObjectId',
      'transactionObjectId',
      'tariffId',
    ];
    sidePanelKeys.forEach((panelKey) => {
      newParameters.delete(panelKey);
    });
    newParameters.set('stationId', stationId);
    setSearchParameters(newParameters);
  };

  /**
   * Open device side panel
   * @param {any} session - The session
   */
  const handleOpenDeviceSidePanel = (session: any) => {
    const deviceId =
      session?.chargingPoint ||
      session?.id ||
      session?.deviceId ||
      session?._id ||
      session?.chargingPointSn;
    if (!deviceId) return;
    const newParameters = new URLSearchParams(searchParameters);
    const sidePanelKeys = [
      'accountCustomId',
      'deviceId',
      'deviceHubId',
      'connectorDeviceId',
      'stationId',
      'facilityId',
      'userCustomId',
      'userId',
      'utilityId',
      'transactionId',
      'personalChargerId',
      'payoutId',
      'cdrObjectId',
      'transactionObjectId',
      'tariffId',
    ];
    sidePanelKeys.forEach((panelKey) => {
      newParameters.delete(panelKey);
    });
    newParameters.set('deviceId', deviceId);
    setSearchParameters(newParameters);
  };

  /**
   * Internal default handler to open station details page
   * @param {any} session - The session
   */
  const handleOpenStationDetails = (session: any) => {
    const stationId = session?.chargingStation || session?.chargingStationId || session?._id;
    const name = session?.chargingStationName || session?.name || session?.stationName;
    if (!stationId) return;

    const searchParametersWithoutKey = new URLSearchParams(location.search);
    searchParametersWithoutKey.delete('stationId');
    searchParametersWithoutKey.delete('action');
    const cleanSource =
      location.pathname +
      (searchParametersWithoutKey.toString() ? `?${searchParametersWithoutKey.toString()}` : '');

    navigate(`/ev-admin/station-management/${stationId}/station-dashboard?searchBy=${name || ''}`, {
      state: { source: cleanSource },
    });
  };

  /**
   * Internal default handler to open device details page
   * @param {any} session - The session
   */
  const handleOpenDeviceDetails = (session: any) => {
    const deviceId =
      session?.id ||
      session?.deviceId ||
      session?._id ||
      session?.chargingPointSn ||
      session?.chargingPointId ||
      session?.chargingPoint;
    const name = session?.chargingPointName || session?.name || session?.chargingDeviceName;
    if (!deviceId) return;

    const searchParametersWithoutKey = new URLSearchParams(location.search);
    searchParametersWithoutKey.delete('deviceId');
    searchParametersWithoutKey.delete('action');
    const cleanSource =
      location.pathname +
      (searchParametersWithoutKey.toString() ? `?${searchParametersWithoutKey.toString()}` : '');

    navigate(`/ev-admin/charger-management/${deviceId}/device-details?searchBy=${name || ''}`, {
      state: { source: cleanSource },
    });
  };

  /**
   * Internal default handler to open end consumer profile page
   * @param {string} userCustomId - The userCustomId
   */
  const handleOpenEndConsumerDetails = (userCustomId: string) => {
    if (!userCustomId) return;
    const endConsumerType =
      searchParameters.get('endConsumerType') ||
      (location.pathname.includes('personal') ? 'personal' : 'commercial');

    const searchParametersWithoutKey = new URLSearchParams(location.search);
    searchParametersWithoutKey.delete('userCustomId');
    searchParametersWithoutKey.delete('action');
    const cleanSource =
      location.pathname +
      (searchParametersWithoutKey.toString() ? `?${searchParametersWithoutKey.toString()}` : '');

    navigate(`/ev-admin/end-consumer/${endConsumerType}/profile/${userCustomId}`, {
      state: {
        source: cleanSource,
      },
    });
  };

  /**
   * Handle changes to edit mode by updating the URL 'action' parameter.
   * isEditMode is derived from the URL so no separate setState is required.
   * @param {any} isEdit - The isEdit
   */
  const handleEditModeChange = (isEdit: boolean) => {
    const newParameters = new URLSearchParams(searchParameters);
    if (isEdit) {
      newParameters.set('action', 'edit');
    } else {
      newParameters.delete('action');
    }
    setSearchParameters(newParameters);
  };

  /**
   * Close side panel and clean up related query params
   */
  const handleSidePanelClose = () => {
    const parameters = new URLSearchParams(searchParameters);
    parameters.delete('action');
    parameters.delete('stationId');
    parameters.delete('deviceId');
    parameters.delete('deviceHubId');
    parameters.delete('connectorDeviceId');
    parameters.delete('personalChargerId');
    parameters.delete('accountCustomId');
    parameters.delete('cdrObjectId');
    parameters.delete('cdrId');
    parameters.delete('id');
    parameters.delete('tariffId');
    parameters.delete('sessionId');
    parameters.delete('userCustomId');
    parameters.delete('userId');
    parameters.delete('action');
    parameters.delete('transactionObjectId');
    setSearchParameters(parameters, { replace: true });
  };

  /**
   * Dispatches notifications to both slices to support all consumer patterns
   * @param {any} type - The type
   * @param {any} message - The message
   * @param {any} code - The code
   */
  const triggerNotification = (type: 'success' | 'error', message: string, code?: number) => {
    dispatch(
      setNotificationMessage({
        status: true,
        message,
        type,
        code: code || 0,
      }),
    );
    dispatch(
      setNotificationDetails({
        type,
        notificationDetails: {
          statusCode: code || (type === 'success' ? 200 : 404),
          message,
        },
      }),
    );
  };

  /**
   * Handle confirm delete station
   * @param {any} stationId - The stationId
   */
  const handleDeleteStationConfirm = async (stationId: string) => {
    try {
      await chargingStation.deleteStation(stationId);
      setOpenStatus(false);
      triggerNotification('success', t('sidePanel:stationDeletedSuccess'));
      const parameters = new URLSearchParams(searchParameters);
      parameters.delete('stationId');
      parameters.delete('action');
      setSearchParameters(parameters);
      onRefresh?.();

      if (onDeleteSuccess) {
        onDeleteSuccess('station', stationId);
      } else {
        setModalView({
          title: '',
          header: false,
          rightCorner: '',
          notification: false,
          content: (
            <SuccessNotification
              title={t('locationDeletion')}
              description={t('locationDeletedSuccessfully')}
              notificationCloseTimer={3000}
              onNotificationClose={() => setOpenStatus(false)}
            />
          ),
        });
        setOpenStatus(true);
      }
    } catch (error: any) {
      triggerNotification(
        'error',
        error?.response?.data?.message || t('errorMessage'),
        error?.response?.data?.code || 0,
      );
    }
  };

  /**
   * Handle delete station click
   * @param {any} stationId - The stationId
   */
  const handleDeleteStationClick = (stationId: string) => {
    setModalView({
      title: t('sidePanel:deleteStation'),
      header: false,
      rightCorner: '',
      notification: false,
      content: (
        <DeleteWarning
          selectedRowId={stationId}
          title={t('sidePanel:deleteStation')}
          description={t('sidePanel:deleteStationWarning')}
          cancelButtonOnClick={() => setOpenStatus(false)}
          submitButtonOnClick={() => handleDeleteStationConfirm(stationId)}
        />
      ),
    });
    setOpenStatus(true);
  };

  /**
   * Handle confirm delete device
   * @param {any} deviceId - The deviceId
   */
  const handleDeleteDeviceConfirm = async (deviceId: string) => {
    try {
      await deviceApi.deleteDevice(deviceId);
      setOpenStatus(false);
      triggerNotification(
        'success',
        t('sidePanel:deviceDeletedSuccess', 'Device deleted successfully!'),
      );
      const parameters = new URLSearchParams(searchParameters);
      parameters.delete('deviceId');
      parameters.delete('action');
      setSearchParameters(parameters);
      onRefresh?.();

      if (onDeleteSuccess) {
        onDeleteSuccess('device', deviceId);
      } else {
        setModalView({
          title: '',
          header: false,
          rightCorner: '',
          notification: false,
          content: (
            <SuccessNotification
              title={t('DeviceDeletion')}
              description={t('deviceDeletedSuccessfully')}
              notificationCloseTimer={3000}
              onNotificationClose={() => setOpenStatus(false)}
            />
          ),
        });
        setOpenStatus(true);
      }
    } catch (error: any) {
      triggerNotification(
        'error',
        error?.response?.data?.message || t('errorMessage'),
        error?.response?.data?.code || 0,
      );
    }
  };

  /**
   * Handle delete device click
   * @param {any} deviceId - The deviceId
   */
  const handleDeleteDeviceClick = (deviceId: string) => {
    setModalView({
      title: t('sidePanel:deleteDevice', 'Delete Device'),
      header: false,
      rightCorner: '',
      notification: false,
      content: (
        <DeleteWarning
          selectedRowId={deviceId}
          title={t('sidePanel:deleteConfirmTitle', 'Delete Confirmation')}
          description={t(
            'sidePanel:deleteConfirmDescription',
            'Are you sure you want to delete this device?',
          )}
          cancelButtonOnClick={() => setOpenStatus(false)}
          submitButtonOnClick={() => handleDeleteDeviceConfirm(deviceId)}
        />
      ),
    });
    setOpenStatus(true);
  };

  /**
   * Handle confirm delete user
   * @param {string} userId - The ID of the user to delete
   * @returns {Promise<void>} Resolves when deletion is processed
   */
  const handleDeleteUserConfirm = async (userId: string) => {
    try {
      await userApi.deleteUser(userId);
      setOpenStatus(false);
      triggerNotification(
        'success',
        t('sidePanel:userDeletedSuccess', 'User deleted successfully!'),
      );
      const parameters = new URLSearchParams(searchParameters);
      parameters.delete('userId');
      parameters.delete('action');
      setSearchParameters(parameters);
      onRefresh?.();
    } catch (error: any) {
      triggerNotification(
        'error',
        error?.response?.data?.message || t('errorMessage'),
        error?.response?.data?.code || 0,
      );
    }
  };

  /**
   * Handle delete user click
   * @param {string} userId - The ID of the user to delete
   * @returns {void}
   */
  const handleDeleteUserClick = (userId: string) => {
    setModalView({
      title: t('sidePanel:deleteUser', 'Delete User'),
      header: false,
      rightCorner: '',
      notification: false,
      content: (
        <DeleteWarning
          selectedRowId={userId}
          title={t('sidePanel:deleteUser', 'Delete User')}
          description={t(
            'sidePanel:deleteUserWarning',
            'Are you sure you want to delete this user?',
          )}
          cancelButtonOnClick={() => setOpenStatus(false)}
          submitButtonOnClick={() => handleDeleteUserConfirm(userId)}
        />
      ),
    });
    setOpenStatus(true);
  };

  /**
   * Returns side panel header content: title and edit/delete/expand actions if applicable.
   * @returns {React.JSX.Element | null} Side panel header content
   */
  const renderSidePanelTopLeftContent = () => {
    const accountCustomId = searchParameters.get('accountCustomId');
    if (accountCustomId) {
      const session = sessions.find((s) => s.accountCustomId === accountCustomId);
      return (
        <div className="side-panel-header-left-title-actions">
          <p className="side-panel-header-title">
            {isEditMode ? t('account:editFacility') : t('account:facilityDetails')}
          </p>
          <div className="side-panel-header-left-icons">
            {!isEditMode && (
              <>
                {/* Expand Pages */}
                {onOpenAccountDetails !== null &&
                  (permissionCheck('cpoPartnerView') ||
                    permissionCheck('cpoPartnerUpdate') ||
                    permissionCheck('partnerAccountView')) && (
                    <div className="button-wrap">
                      <span
                        role="presentation"
                        className="access-facility"
                        onClick={() => {
                          if (onOpenAccountDetails) {
                            onOpenAccountDetails(session || { accountCustomId }, false);
                          } else {
                            handleOpenAccountDetails(session || { accountCustomId });
                          }
                        }}>
                        <ExpandIcon className="icon" />
                      </span>
                      <div className="hover-label">
                        {t('sidePanel:expandPages', 'Expand Pages')}
                      </div>
                    </div>
                  )}
                {!disableEdit &&
                  (permissionCheck('cpoPartnerUpdate') ||
                    permissionCheck('partnerAccountUpdate')) && (
                    <div className="button-wrap">
                      <span
                        role="presentation"
                        className="access-facility"
                        onClick={() => handleEditModeChange(true)}>
                        <EditIcon className="icon" />
                      </span>
                      <div className="hover-label">{t('account:edit')}</div>
                    </div>
                  )}
              </>
            )}
          </div>
        </div>
      );
    }

    const stationId = searchParameters.get('stationId');
    if (stationId) {
      const session = sessions.find(
        (s: any) =>
          s.chargingStationId === stationId ||
          s.chargingStation === stationId ||
          s._id === stationId ||
          s.stationName === stationId ||
          s.name === stationId,
      );
      return (
        <div className="side-panel-header-left-title-actions">
          <p className="side-panel-header-title">
            {isEditMode
              ? t('sidePanel:editStationDetails', 'Edit Station Details')
              : t('sidePanel:stationDetails')}
          </p>
          <div className="side-panel-header-left-icons">
            {!isEditMode && (
              <>
                {onOpenStationDetails !== null && (
                  <div className="button-wrap">
                    <span
                      role="presentation"
                      className="access-facility"
                      onClick={() => {
                        if (onOpenStationDetails) {
                          onOpenStationDetails(session || { chargingStationId: stationId }, false);
                        } else {
                          handleOpenStationDetails(session || { chargingStationId: stationId });
                        }
                      }}>
                      <ExpandIcon className="icon" />
                    </span>
                    <div className="hover-label">{t('sidePanel:expandPages', 'Expand Pages')}</div>
                  </div>
                )}
                {!disableEdit &&
                  (permissionCheck('partnerChargingStationUpdate') ||
                    permissionCheck('cpoChargingStationUpdate')) && (
                    <div className="button-wrap">
                      <span
                        role="presentation"
                        className="access-facility"
                        onClick={() => handleEditModeChange(true)}>
                        <EditIcon className="icon" />
                      </span>
                      <div className="hover-label">{t('sidePanel:edit', 'Edit')}</div>
                    </div>
                  )}
                {!disableDelete &&
                  (permissionCheck('partnerChargingStationDelete') ||
                    permissionCheck('cpoChargingStationDelete')) && (
                    <div className="button-wrap">
                      <span
                        role="presentation"
                        className="access-facility"
                        onClick={() => handleDeleteStationClick(stationId)}>
                        <DeleteIcon className="icon delete" />
                      </span>
                      <div className="hover-label">{t('sidePanel:delete', 'Delete')}</div>
                    </div>
                  )}
              </>
            )}
          </div>
        </div>
      );
    }

    const connectorDeviceId = searchParameters.get('connectorDeviceId');
    if (connectorDeviceId) {
      return (
        <div className="side-panel-header-left-title-actions">
          <p className="side-panel-header-title">{t('sidePanel:connectors', 'Connectors')}</p>
        </div>
      );
    }

    const deviceHubId = searchParameters.get('deviceHubId');
    if (deviceHubId) {
      const session = sessions.find(
        (s: any) =>
          s._id === deviceHubId ||
          s.id === deviceHubId ||
          s.deviceId === deviceHubId ||
          s.deviceHubId === deviceHubId ||
          s.serialNumber === deviceHubId ||
          s.chargingPointSn === deviceHubId,
      );
      const isBlacklisted = session?.status === 'blacklisted';
      const isLinked = session?.deviceOwner && session?.deviceOwner !== '-';
      const hasDeviceHubUpdate = permissionCheck('devicesEvDeviceHubUpdate');
      const hasDeviceHubDelete = permissionCheck('devicesEvDeviceHubDelete');

      return (
        <div className="side-panel-header-left-title-actions">
          <p className="side-panel-header-title">
            {t('sidePanel:deviceDetails', 'Device Details')}
          </p>
          <div className="side-panel-header-left-icons">
            {!isEditMode && (
              <>
                {!disableExpand && (
                  <div className="button-wrap">
                    <button
                      type="button"
                      className="access-facility header-icon-btn"
                      onClick={() => {
                        if (onOpenDeviceDetails) {
                          onOpenDeviceDetails(
                            session || {
                              _id: deviceHubId,
                              deviceId: deviceHubId,
                              deviceHubId,
                            },
                            false,
                          );
                        } else {
                          const searchParametersWithoutKey = new URLSearchParams(location.search);
                          searchParametersWithoutKey.delete('deviceHubId');
                          searchParametersWithoutKey.delete('action');
                          const cleanSource =
                            location.pathname +
                            (searchParametersWithoutKey.toString()
                              ? `?${searchParametersWithoutKey.toString()}`
                              : '');
                          navigate(`/ev-admin/ev-device-hub/device-details/${deviceHubId}`, {
                            state: { source: cleanSource },
                          });
                        }
                      }}>
                      <ExpandIcon className="icon" />
                    </button>
                    <div className="hover-label">{t('sidePanel:expandPages', 'Expand Pages')}</div>
                  </div>
                )}
                {!disableEdit && (hasDeviceHubUpdate || Boolean(onEditDevice) || true) && (
                  <div className="button-wrap">
                    <button
                      type="button"
                      className="access-facility header-icon-btn"
                      disabled={Boolean(isLinked && hasDeviceHubDelete)}
                      onClick={() => {
                        if (onEditDevice) {
                          onEditDevice(
                            session || {
                              _id: deviceHubId,
                              deviceId: deviceHubId,
                              deviceHubId,
                            },
                          );
                        } else {
                          handleEditModeChange(true);
                        }
                      }}
                      title={
                        isLinked && hasDeviceHubDelete
                          ? t(
                              'sidePanel:deleteOrEditRestrictionTooltip',
                              'Delete or Edit is restricted',
                            )
                          : t('sidePanel:edit', 'Edit')
                      }>
                      <EditIcon className="icon" />
                    </button>
                    <div className="hover-label">{t('sidePanel:edit', 'Edit')}</div>
                  </div>
                )}
                {Boolean(onStatusChange) && (
                  <div className="button-wrap">
                    <button
                      type="button"
                      className="access-facility header-icon-btn"
                      onClick={() =>
                        onStatusChange?.(
                          session || {
                            _id: deviceHubId,
                            deviceId: deviceHubId,
                            deviceHubId,
                            status: session?.status,
                          },
                        )
                      }
                      title={
                        isBlacklisted
                          ? t('sidePanel:whitelistDevice', 'Whitelist Device')
                          : t('sidePanel:blacklistDevice', 'Blacklist Device')
                      }>
                      {isBlacklisted ? (
                        <TickCircleIcon className="icon" />
                      ) : (
                        <SlashIcon className="icon" />
                      )}
                    </button>
                    <div className="hover-label">
                      {isBlacklisted
                        ? t('sidePanel:whitelist', 'Whitelist')
                        : t('sidePanel:blacklist', 'Blacklist')}
                    </div>
                  </div>
                )}
                {!disableDelete && (hasDeviceHubDelete || Boolean(onDeleteDevice) || true) && (
                  <div className="button-wrap">
                    <button
                      type="button"
                      className="access-facility header-icon-btn delete-btn"
                      disabled={Boolean(isLinked && hasDeviceHubDelete)}
                      onClick={() => {
                        if (onDeleteDevice) {
                          onDeleteDevice(
                            session || {
                              _id: deviceHubId,
                              deviceId: deviceHubId,
                              deviceHubId,
                            },
                          );
                        } else {
                          handleDeleteDeviceClick(deviceHubId);
                        }
                      }}
                      title={
                        isLinked && hasDeviceHubDelete
                          ? t(
                              'sidePanel:deleteOrEditRestrictionTooltip',
                              'Delete or Edit is restricted',
                            )
                          : t('sidePanel:delete', 'Delete')
                      }>
                      <DeleteIcon className="icon delete" />
                    </button>
                    <div className="hover-label">{t('sidePanel:delete', 'Delete')}</div>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      );
    }

    const deviceId = searchParameters.get('deviceId');
    if (deviceId) {
      const session = sessions.find(
        (s: any) =>
          s.deviceId === deviceId ||
          s.id === deviceId ||
          s._id === deviceId ||
          s.chargingPointSn === deviceId ||
          s.chargingPointName === deviceId ||
          s.chargingPoint === deviceId,
      );
      return (
        <div className="side-panel-header-left-title-actions">
          <p className="side-panel-header-title">
            {isEditMode ? t('sidePanel:editDevice', 'Edit Device') : t('sidePanel:deviceDetails')}
          </p>
          <div className="side-panel-header-left-icons">
            {!isEditMode && (
              <>
                {onOpenDeviceDetails !== null && (
                  <div className="button-wrap">
                    <span
                      role="presentation"
                      className="access-facility"
                      onClick={() => {
                        if (onOpenDeviceDetails) {
                          onOpenDeviceDetails(session || { deviceId }, false);
                        } else {
                          handleOpenDeviceDetails(session || { deviceId });
                        }
                      }}>
                      <ExpandIcon className="icon" />
                    </span>
                    <div className="hover-label">{t('sidePanel:expandPages', 'Expand Pages')}</div>
                  </div>
                )}
                {!disableEdit &&
                  (permissionCheck('partnerChargingDevicesUpdate') ||
                    permissionCheck('cpoChargingPointUpdate')) && (
                    <div className="button-wrap">
                      <span
                        role="presentation"
                        className="access-facility"
                        onClick={() => handleEditModeChange(true)}>
                        <EditIcon className="icon" />
                      </span>
                      <div className="hover-label">{t('sidePanel:edit', 'Edit')}</div>
                    </div>
                  )}
                {!disableDelete &&
                  (permissionCheck('partnerChargingDevicesDelete') ||
                    permissionCheck('cpoChargingPointDelete')) && (
                    <div className="button-wrap">
                      <span
                        role="presentation"
                        className="access-facility"
                        onClick={() => handleDeleteDeviceClick(deviceId)}>
                        <DeleteIcon className="icon delete" />
                      </span>
                      <div className="hover-label">{t('sidePanel:delete', 'Delete')}</div>
                    </div>
                  )}
              </>
            )}
          </div>
        </div>
      );
    }

    const userId = searchParameters.get('userId');
    if (userId) {
      return (
        <div className="side-panel-header-left-title-actions">
          <p className="side-panel-header-title">
            {isEditMode
              ? t('sidePanel:editUser', 'Edit User')
              : t('sidePanel:userDetails', 'User Details')}
          </p>
          <div className="side-panel-header-left-icons">
            {!isEditMode && (
              <>
                {!disableEdit && permissionCheck('userManagementUpdate') && (
                  <div className="button-wrap">
                    <span
                      role="presentation"
                      className="access-facility"
                      onClick={() => handleEditModeChange(true)}>
                      <EditIcon className="icon" />
                    </span>
                    <div className="hover-label">{t('sidePanel:edit', 'Edit')}</div>
                  </div>
                )}
                {!disableDelete && permissionCheck('userManagementDelete') && (
                  <div className="button-wrap">
                    <span
                      role="presentation"
                      className="access-facility"
                      onClick={() => handleDeleteUserClick(userId)}>
                      <DeleteIcon className="icon delete" />
                    </span>
                    <div className="hover-label">{t('sidePanel:delete', 'Delete')}</div>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      );
    }

    const userCustomId = searchParameters.get('userCustomId');
    if (userCustomId) {
      const hasProfileParameter =
        searchParameters.has('profile') ||
        Boolean(searchParameters.get('profile')) ||
        location.search.toLowerCase().includes('profile') ||
        location.pathname.toLowerCase().includes('profile');

      return (
        <div className="side-panel-header-left-title-actions">
          <p className="side-panel-header-title">{t('driverDetails', 'Driver Details')}</p>
          <div className="side-panel-header-left-icons">
            {!disableExpand && onOpenEndConsumerDetails !== null && !hasProfileParameter && (
              <div className="button-wrap">
                <span
                  role="presentation"
                  className="access-facility"
                  onClick={() => {
                    if (onOpenEndConsumerDetails) {
                      onOpenEndConsumerDetails(userCustomId, false);
                    } else {
                      handleOpenEndConsumerDetails(userCustomId);
                    }
                  }}>
                  <ExpandIcon className="icon" />
                </span>
                <div className="hover-label">{t('sidePanel:expandPages', 'Expand Pages')}</div>
              </div>
            )}

            {!disableEdit &&
              (permissionCheck('endConsumerUpdate') ||
                permissionCheck('userManagementUpdate') ||
                permissionCheck('userUpdate') ||
                true) && (
                <div className="button-wrap">
                  <span
                    role="presentation"
                    className="access-facility"
                    onClick={() => handleEditModeChange(true)}>
                    <EditIcon className="icon" />
                  </span>
                  <div className="hover-label">{t('sidePanel:edit', 'Edit')}</div>
                </div>
              )}
            {!disableDelete &&
              (permissionCheck('endConsumerDelete') ||
                permissionCheck('userManagementDelete') ||
                permissionCheck('userDelete') ||
                true) && (
                <div className="button-wrap">
                  <span
                    role="presentation"
                    className="access-facility"
                    onClick={() => handleDeleteUserClick(userCustomId)}>
                    <DeleteIcon className="icon delete" />
                  </span>
                  <div className="hover-label">{t('sidePanel:delete', 'Delete')}</div>
                </div>
              )}
          </div>
        </div>
      );
    }

    if (searchParameters.get('cdrObjectId') || searchParameters.get('cdrId')) {
      return (
        <div className="side-panel-header-left-title-actions">
          <p className="side-panel-header-title flex-title">
            {t('sidePanel:transactionDetails', 'Transaction Details')}
          </p>
        </div>
      );
    }

    if (searchParameters.get('transactionObjectId') || searchParameters.get('transactionId')) {
      return (
        <div className="side-panel-header-left-title-actions">
          <p className="side-panel-header-title flex-title">
            {t('sidePanel:transactionDetails', 'Transaction Details')}
          </p>
        </div>
      );
    }

    if (searchParameters.get('sessionId') || searchParameters.get('id')) {
      return (
        <div className="side-panel-header-left-title-actions">
          <p className="side-panel-header-title flex-title">
            {t('sidePanel:sessionDetails', 'Session Details')}
          </p>
        </div>
      );
    }

    if (searchParameters.get('preauthId')) {
      return (
        <div className="side-panel-header-left-title-actions">
          <p className="side-panel-header-title flex-title">
            {t('sidePanel:preauthDetails', 'Preauth Details')}
          </p>
        </div>
      );
    }

    const tariffId = searchParameters.get('tariffId');
    if (tariffId) {
      const session = sessions.find(
        (s: any) =>
          s?.id === tariffId ||
          s?._id === tariffId ||
          s?.tariffId === tariffId ||
          s?.tariffs?.some(
            (item: any) =>
              item?.tariffId === tariffId || item?._id === tariffId || item?.id === tariffId,
          ),
      );
      const tariffStatus =
        session?.status ||
        session?.tariffs?.find(
          (item: any) =>
            item?.tariffId === tariffId || item?._id === tariffId || item?.id === tariffId,
        )?.status;
      const isExpired = tariffStatus === 'expired' || tariffStatus?.toLowerCase() === 'expired';

      return (
        <div className="side-panel-header-left-title-actions">
          <p className="side-panel-header-title">
            {t('sidePanel:tariffDetails', 'Tariff Details')}
          </p>
          <div className="side-panel-header-left-icons">
            <div className="button-wrap">
              <span
                role="presentation"
                className="access-facility"
                onClick={() =>
                  navigate(
                    `/ev-admin/tariffs/view/${tariffId}${isPartner ? '?isPartner=true' : ''}`,
                  )
                }>
                <ExpandIcon className="icon" />
              </span>
              <div className="hover-label">{t('sidePanel:expandPages', 'Expand Pages')}</div>
            </div>
            <div className="button-wrap">
              <span
                role="presentation"
                className="access-facility"
                onClick={() =>
                  window.open(
                    `/ev-admin/tariffs/view/${tariffId}${isPartner ? '?isPartner=true' : ''}`,
                    '_blank',
                  )
                }>
                <NewTabIcon className="icon" />
              </span>
              <div className="hover-label">{t('sidePanel:expandNewPages', 'Expand New Pages')}</div>
            </div>
            {!disableEdit &&
              !isExpired &&
              (permissionCheck('partnerTariffUpdate') || permissionCheck('cpoTariffUpdate')) && (
                <div className="button-wrap">
                  <span
                    role="presentation"
                    className="access-facility"
                    onClick={() =>
                      navigate(
                        `/ev-admin/tariffs/edit-tariff/${tariffId}${isPartner ? '?isPartner=true' : ''}`,
                      )
                    }>
                    <EditIcon className="icon" />
                  </span>
                  <div className="hover-label">{t('sidePanel:edit', 'Edit')}</div>
                </div>
              )}
          </div>
        </div>
      );
    }

    if (searchParameters.get('personalChargerId')) {
      return (
        <div className="side-panel-header-left-title-actions">
          <p className="side-panel-header-title">
            {t('devices:persnalChargers', 'Personal Chargers')}
          </p>
        </div>
      );
    }
    return null;
  };

  return {
    isEditMode,
    openStatus,
    setOpenStatus,
    modalView,
    setModalView,
    handleEditModeChange,
    handleSidePanelClose,
    handleDeleteStationClick,
    handleDeleteStationConfirm,
    handleDeleteDeviceClick,
    handleDeleteDeviceConfirm,
    handleDeleteUserClick,
    handleDeleteUserConfirm,
    renderSidePanelTopLeftContent,
    handleOpenAccountDetails,
    handleOpenStationDetails,
    handleOpenStationSidePanel,
    handleOpenDeviceSidePanel,
    handleOpenDeviceDetails,
    handleOpenEndConsumerDetails,
  };
};
